import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { getAccessToken } from '@privy-io/react-auth';

export const ErrorCodes = {
  MFA_REQUIRED: 428,
  MFA_SETUP_REQUIRED: 422,
  INVALID_MFA_CODE: 412,
  LOCKED: 423,
};

/**
 * Flattens nested objects into bracket notation query parameters (like axios/qs)
 * Example: { filters: { game: ['poker', 'holdem'], buyIn: { min: 10 } } }
 * Becomes: { 'filters[game][0]': 'poker', 'filters[game][1]': 'holdem', 'filters[buyIn][min]': 10 }
 */
const flattenQueryParams = (params: Record<string, any>, prefix = ''): Record<string, any> => {
  const flattened: Record<string, any> = {};

  Object.keys(params).forEach((key) => {
    const value = params[key];
    const newKey = prefix ? `${prefix}[${key}]` : key;

    if (value === null || value === undefined) {
      // Skip null/undefined values
      return;
    }

    if (value instanceof Date) {
      // Convert dates to ISO strings
      flattened[newKey] = value.toISOString();
    } else if (Array.isArray(value)) {
      // Handle arrays with bracket notation
      value.forEach((item, index) => {
        if (typeof item === 'object' && !(item instanceof Date)) {
          // Recursively flatten objects in arrays
          Object.assign(flattened, flattenQueryParams(item, `${newKey}[${index}]`));
        } else {
          // Primitive values in arrays
          flattened[`${newKey}[${index}]`] = item;
        }
      });
    } else if (typeof value === 'object') {
      // Recursively flatten nested objects
      Object.assign(flattened, flattenQueryParams(value, newKey));
    } else {
      // Primitive values
      flattened[newKey] = value;
    }
  });

  return flattened;
};

export const createQueryHandler = (url: string, method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH' = 'GET') => {
  return (args: any) => {
    const { headers, meta, mfaCode, query, ...restArgs } = args || ({} as any);

    // Handle dynamic route parameters (e.g., /table/:tableId)
    let processedUrl = url;
    const urlParams: Record<string, any> = {};
    const bodyParams: Record<string, any> = {};

    // Extract route parameters and replace them in the URL
    for (const [key, value] of Object.entries(restArgs || {})) {
      const paramPattern = `:${key}`;
      if (processedUrl.includes(paramPattern)) {
        processedUrl = processedUrl.replace(paramPattern, String(value));
        urlParams[key] = value;
      } else {
        bodyParams[key] = value;
      }
    }

    // Handle query parameters for GET requests
    let queryParams = {};
    if (method === 'GET') {
      // If we have a query object, flatten it
      if (query && typeof query === 'object') {
        queryParams = flattenQueryParams(query);
      }
    }

    return {
      url: processedUrl,
      method,
      body: method !== 'GET' ? bodyParams : undefined,
      params: method === 'GET' ? queryParams : undefined,
      headers: {
        ...headers,
        ...(mfaCode && { 'x-totp-code': mfaCode }),
      },
    };
  };
};

export const api = createApi({
  reducerPath: 'api',
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_PHENOM_API || '',
    prepareHeaders: async (headers) => {
      try {
        const authToken = await getAccessToken();
        if (authToken) {
          headers.set('Authorization', `Bearer ${authToken}`);
        }
      } catch (error) {
        console.error('Failed to get access token:', error);
      }

      headers.set('Content-Type', 'application/json');
      return headers;
    },
    credentials: 'include',
  }),
  tagTypes: [
    'User',
    'Mfa',
    'DepositBonus',
    'UserDepositBonus',
    'Transaction',
    'Table',
    'Tournament',
    'TournamentTemplate',
    'TournamentSchedule',
    'BlindStructure',
    'TournamentTicket',
  ],
  endpoints: () => ({}),
});

export type ApiState = ReturnType<typeof api.reducer>;
